import { useCallback, useState, useEffect } from 'react';
import styles from './index.module.scss';
import {
  Form,
  Modal,
  Input,
  message,
  Popconfirm,
  Button,
  Radio,
  Select,
  Upload,
} from 'antd';
import { OmsRecycleOrderDetail } from 'types/oms';
import { fetchPaymentVoucher } from 'apis/oms';
import { PAYMENT_ACCOUNT_OPTIONS } from 'constants/RecyclingConsignment';
import { UploadOutlined } from '@ant-design/icons';
import { thousands } from 'utils/tools';
import i18n from 'i18next';
import LOCALS from '../../../../commons/locals';
import type { UploadFile, UploadChangeParam } from 'antd/es/upload/interface';
import type { RcFile } from 'antd/es/upload';
import {
  extractClipboardImageFiles,
  fileToUploadFile,
} from './clipboard-image';
import { getBase64, mergeImages } from './image-utils';

interface Props {
  open: boolean;
  onClose: () => void;
  data: OmsRecycleOrderDetail;
  reload: () => void;
  modalOption: any;
}

/** 录入打款信息 */
const PaymentVoucherModal = ({
  open,
  onClose,
  data,
  reload,
  modalOption,
}: Props) => {
  const [form] = Form.useForm<any>();
  const [loading, setLoading] = useState(false);
  const [voucherFileList, setVoucherFileList] = useState<UploadFile[]>([]);

  const { type } = modalOption;

  useEffect(() => {
    if (!open) return;

    const onPaste = (e: ClipboardEvent) => {
      const images = extractClipboardImageFiles(e);
      if (images.length === 0) return;

      // 有图片时优先走凭证上传逻辑，避免把图片“粘贴”进页面其它地方
      e.preventDefault();

      // 追加到现有列表，而不是替换
      const newFiles = images.map((img) => fileToUploadFile(img));
      setVoucherFileList((prev) => [...prev, ...newFiles]);
      message.success(
        i18n.t(LOCALS.payment_voucher_clipboard_paste_success, {
          count: images.length,
          mergeNotice:
            images.length > 1
              ? i18n.t(LOCALS.payment_voucher_clipboard_merge_notice)
              : '',
        }),
      );
    };

    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [open]);

  const selectedPaymentMethod = Form.useWatch('settlementType', form);
  const handleVoucherChange = useCallback(
    (info: UploadChangeParam<UploadFile>) => {
      // 允许多个文件，不再限制为最后一个
      const normalized = info.fileList.map((file) =>
        file.originFileObj ? { ...file, status: 'done' as const } : file,
      );
      setVoucherFileList(normalized);
    },
    [],
  );

  const handleVoucherPreview = useCallback(async (file: UploadFile) => {
    if (file.url) {
      window.open(file.url);
      return;
    }
    if (file.originFileObj) {
      const src = await getBase64(file.originFileObj as RcFile);
      const image = new Image();
      image.src = src;
      const imgWindow = window.open(src);
      imgWindow?.document.write(image.outerHTML);
    }
  }, []);

  const onFinish = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const {
        settlementType,
        paymentAccount,
        financialPaymentInfo,
        financialPaymentAmount,
        isOrderCompleted,
      } = values;
      const id = data?.omsRecycleOrder?.id;
      if (!id) {
        message.error(i18n.t(LOCALS.payment_voucher_order_missing));
        setLoading(false);
        return;
      }
      const cleanAmount =
        typeof financialPaymentAmount === 'string'
          ? financialPaymentAmount.replace(/,/g, '')
          : undefined;
      const formData = new FormData();
      formData.append('id', `${id}`);
      formData.append('settlementType', String(settlementType));
      formData.append('isOrderCompleted', String(isOrderCompleted));
      if (paymentAccount) {
        formData.append('paymentAccount', paymentAccount);
      }
      if (financialPaymentInfo) {
        formData.append('financialPaymentInfo', financialPaymentInfo);
      }
      if (cleanAmount) {
        formData.append('financialPaymentAmount', cleanAmount);
      }

      // 获取所有新上传的图片文件
      const imageFiles = voucherFileList
        .filter((file) => file.originFileObj instanceof Blob)
        .map((file) => file.originFileObj as Blob);

      if (imageFiles.length > 0) {
        try {
          // 如果有多张图片，合成为一张
          const mergedBlob = await mergeImages(imageFiles);
          const mergedFile = new File(
            [mergedBlob],
            `payment-voucher-${Date.now()}.jpg`,
            { type: 'image/jpeg' },
          );
          formData.append('file', mergedFile);

          if (imageFiles.length > 1) {
            message.success(
              i18n.t(LOCALS.payment_voucher_merge_success, {
                count: imageFiles.length,
              }),
            );
          }
        } catch (error) {
          message.error(i18n.t(LOCALS.payment_voucher_merge_failed));
          setLoading(false);
          return;
        }
      }

      fetchPaymentVoucher(formData)
        .then(() => {
          message.success(i18n.t(LOCALS.submission_success));
          onClose();
          reload();
        })
        .catch(() => {
          setLoading(false);
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (error) {
      setLoading(false);
    }
  }, [data, form, onClose, reload, voucherFileList]);

  useEffect(() => {
    if (Object.keys(data).length === 0) return;
    const { omsRecycleOrder, omsRecycleOrderItem } = data;
    const { financialPaymentVoucher = '', type = 0 } = omsRecycleOrder || {};
    const { finalRecyclePrice, finalSalePrice } = omsRecycleOrderItem || {};
    // 0-未确认，1-寄卖，2-回收
    let PaymentAmount = 0;
    if (type === 1) {
      PaymentAmount = finalSalePrice || 0;
    } else if (type === 2) {
      PaymentAmount = finalRecyclePrice || 0;
    }

    //
    form.setFieldsValue({
      financialPaymentInfo: omsRecycleOrder?.payoutDetail || '',
      financialPaymentAmount: thousands(PaymentAmount),
    });
    if (financialPaymentVoucher) {
      try {
        const parsedVoucher = JSON.parse(financialPaymentVoucher);
        const urls = Array.isArray(parsedVoucher)
          ? parsedVoucher
          : [parsedVoucher];
        const validUrls = urls.filter(
          (url): url is string => typeof url === 'string' && url.length > 0,
        );
        if (validUrls.length > 0) {
          setVoucherFileList(
            validUrls.map((url, index) => ({
              uid: `existing-${index}`,
              name: url.split('/').pop() || `voucher-${index + 1}`,
              status: 'done',
              url,
            })),
          );
        } else {
          setVoucherFileList([]);
        }
      } catch (error) {
        setVoucherFileList([
          {
            uid: 'existing-0',
            name: financialPaymentVoucher.split('/').pop() || 'payment-voucher',
            status: 'done',
            url: financialPaymentVoucher,
          },
        ]);
      }
    } else {
      setVoucherFileList([]);
    }
  }, [data, form]);

  const paymentVoucherUploadItem = (
    <Form.Item label={i18n.t(LOCALS.payment_voucher)}>
      <Upload
        accept="image/*"
        listType="picture-card"
        beforeUpload={() => false}
        fileList={voucherFileList}
        onChange={handleVoucherChange}
        onPreview={handleVoucherPreview}
        multiple
        showUploadList={{
          showPreviewIcon: true,
          showRemoveIcon: true,
        }}
      >
        <UploadOutlined />
      </Upload>
      <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 6 }}>
        {i18n.t(LOCALS.payment_voucher_upload_tip)}
      </div>
    </Form.Item>
  );

  return (
    <Modal
      open={open}
      title={
        type === 'create'
          ? i18n.t(LOCALS.confirm_payment)
          : i18n.t(LOCALS.update_payment_information)
      }
      onCancel={() => onClose()}
      onOk={onFinish}
      className={styles.PaymentVoucherModal}
      destroyOnClose
      confirmLoading={loading}
      width={600}
      footer={[
        <Button onClick={() => onClose()} key={'cancel'}>
          {i18n.t(LOCALS.cancel)}
        </Button>,
        <Popconfirm
          title={i18n.t(LOCALS.caution)}
          description={
            <div className={styles.Popconfirm}>
              {i18n.t(LOCALS.confirm_submission)}
            </div>
          }
          onConfirm={() => onFinish()}
          okText={i18n.t(LOCALS.confirm)}
          cancelText={i18n.t(LOCALS.cancel)}
          key={'ok'}
        >
          <Button
            type="primary"
            loading={loading}
            onClick={() => form.validateFields()}
          >
            {i18n.t(LOCALS.confirm)}
          </Button>
        </Popconfirm>,
      ]}
    >
      {/* <BriefProductInfo data={data} showData={showData} /> */}
      {/*TODO 选择现金还是会计打款 1-现金,2-会计打款*/}
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
        initialValues={{
          settlementType: data.omsRecycleOrder?.settlementType ?? 2,
        }}
      >
        <Form.Item
          label={i18n.t(LOCALS.payment_method)}
          name="settlementType"
          rules={[{ required: true }]}
        >
          <Select>
            <Select.Option value={1}>{i18n.t(LOCALS.cash)}</Select.Option>
            <Select.Option value={2}>
              {i18n.t(LOCALS.accounting_settlement)}
            </Select.Option>
          </Select>
        </Form.Item>
      </Form>
      {selectedPaymentMethod === 2 ? (
        <>
          <div className={styles.formWarp}>
            <Form
              form={form}
              layout="horizontal"
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 16 }}
              initialValues={{ isOrderCompleted: 1 }}
            >
              <Form.Item
                label={i18n.t(LOCALS.payment_account)}
                name="paymentAccount"
              >
                <Select
                  placeholder={i18n.t(LOCALS.please_select_payment_account)}
                  allowClear
                  options={
                    PAYMENT_ACCOUNT_OPTIONS[
                      data.omsRecycleOrder?.currency ?? ''
                    ] ?? []
                  }
                />
              </Form.Item>
              <Form.Item
                label={i18n.t(LOCALS.payment_details)}
                name="financialPaymentInfo"
              >
                <Input.TextArea disabled rows={5} />
              </Form.Item>
              <Form.Item
                label={i18n.t(LOCALS.receiving_amount)}
                name="financialPaymentAmount"
              >
                <Input addonBefore={data.omsRecycleOrder?.currency} disabled />
              </Form.Item>
              {paymentVoucherUploadItem}

              <Form.Item
                label={i18n.t(LOCALS.complete_order)}
                name="isOrderCompleted"
                rules={[{ required: true }]}
              >
                <Radio.Group>
                  <Radio value={0}>{i18n.t(LOCALS.no)}</Radio>
                  <Radio value={1}>{i18n.t(LOCALS.yes)}</Radio>
                </Radio.Group>
              </Form.Item>
            </Form>
          </div>
        </>
      ) : (
        <>
          <div className={styles.formWarp}>
            <Form
              form={form}
              layout="horizontal"
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 16 }}
              initialValues={{ isOrderCompleted: 1 }}
            >
              <Form.Item
                label={i18n.t(LOCALS.receiving_amount)}
                name="financialPaymentAmount"
              >
                <Input addonBefore={data.omsRecycleOrder?.currency} disabled />
              </Form.Item>

              {paymentVoucherUploadItem}

              <Form.Item
                label={i18n.t(LOCALS.complete_order)}
                name="isOrderCompleted"
                rules={[{ required: true }]}
              >
                <Radio.Group>
                  <Radio disabled={true} value={0}>
                    {i18n.t(LOCALS.no)}
                  </Radio>
                  <Radio defaultChecked={true} disabled={true} value={1}>
                    {i18n.t(LOCALS.yes)}
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </Form>
          </div>
        </>
      )}
    </Modal>
  );
};

export default PaymentVoucherModal;
